import { useEffect, useState } from "react";
import { toast } from "sonner";
import { gestApi } from "./GestAuthContext";

const FIELDS = [
  {
    key: "ritardo_giorni",
    label: "Ritardo previsto → alert",
    suffix: "giorni",
    hint: "Genera un alert se la previsione stima un ritardo superiore a questi giorni.",
    min: 1, max: 180,
  },
  {
    key: "gap_posa_pct",
    label: "Gap produzione/posa massimo",
    suffix: "%",
    hint: "Alert se la posa è più indietro della produzione di questa percentuale con consegna vicina.",
    min: 0, max: 100,
  },
  {
    key: "gap_posa_days_window",
    label: "Consegna «vicina»",
    suffix: "giorni",
    hint: "Definisce quanti giorni alla consegna sono considerati «critici» per il gap posa/produzione.",
    min: 1, max: 365,
  },
  {
    key: "margine_min_pct",
    label: "Margine minimo",
    suffix: "%",
    hint: "Alert se il margine di un cantiere scende sotto questa percentuale.",
    min: 0, max: 100,
  },
  {
    key: "fase_stalla_giorni",
    label: "Cantiere fermo → alert",
    suffix: "giorni",
    hint: "Alert se un cantiere attivo non ha avanzamenti produzione o posa da questi giorni.",
    min: 1, max: 365,
  },
  {
    key: "min_data_points",
    label: "Dati minimi per previsione",
    suffix: "record",
    hint: "Numero minimo di avanzamenti registrati per calcolare la previsione (altrimenti si dichiara insufficiente).",
    min: 1, max: 50,
  },
];

export default function GestSettings() {
  const [values, setValues] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await gestApi.get("/settings/thresholds");
      setValues(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const update = (k, v) => setValues((s) => ({ ...s, [k]: v === "" ? 0 : Number(v) }));

  const save = async () => {
    setSaving(true);
    try {
      await gestApi.put("/settings/thresholds", values);
      toast.success("Soglie aggiornate. Le nuove regole sono già attive.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-5 md:p-8 max-w-[900px] mx-auto" data-testid="gest-settings-page">
      <div className="mb-6">
        <div className="text-[12px] uppercase tracking-widest text-[#6e6e73]">Impostazioni</div>
        <h1 className="text-[26px] md:text-[34px] font-semibold tracking-[-0.02em] text-[#0a1420]">Soglie di alert e previsione</h1>
        <div className="text-[13px] text-[#6e6e73] mt-1">Le soglie determinano quando il sistema genera avvisi e quando il semaforo 🟢🟡🔴 diventa critico.</div>
      </div>

      {busy || !values ? (
        <div className="text-[#6e6e73]">Caricamento…</div>
      ) : (
        <div className="bg-white rounded-2xl p-5 md:p-6 border border-black/[0.04] shadow-sm">
          <div className="space-y-5">
            {FIELDS.map((f) => (
              <div key={f.key} className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3 md:gap-6 items-start pt-4 first:pt-0 border-t first:border-t-0 border-black/5">
                <div className="min-w-0">
                  <div className="text-[13.5px] font-medium text-[#0a1420]">{f.label}</div>
                  <div className="text-[12px] text-[#6e6e73] mt-0.5 leading-relaxed">{f.hint}</div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={f.min}
                    max={f.max}
                    value={values[f.key] ?? ""}
                    onChange={(e) => update(f.key, e.target.value)}
                    data-testid={`gest-settings-${f.key}`}
                    className="w-24 border border-black/10 rounded-lg px-3 py-2 text-[14px] text-right font-mono focus:border-[#0a1420] outline-none"
                  />
                  <span className="text-[12px] text-[#6e6e73]">{f.suffix}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-black/5 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[11.5px] text-[#6e6e73]">Le modifiche sono immediate: gli alert vengono ricalcolati in tempo reale.</div>
            <div className="flex gap-2">
              <button
                onClick={load}
                disabled={saving}
                className="px-4 py-2 text-[13px] text-[#6e6e73] hover:text-[#0a1420]"
              >Ripristina</button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="gest-settings-save"
                className="px-5 py-2 bg-[#0a1420] text-white rounded-lg text-[13px] font-semibold hover:bg-[#1a2432] disabled:opacity-60"
              >{saving ? "Salvataggio…" : "Salva soglie"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
